export const aiDrivenSolutionData = [
  {
    id: 1,
    title: "AI-Powered Chatbots",
    description:
      "Develop intelligent chatbots for customer service, support, and engagement. Enhance user experience with natural language processing and machine learning capabilities.",
    image: "/case_study_image/case_study_menu/ai_chatbot.png",
    alt: "AI-Powered Chatbots",
    details: [ "24/7 automated customer support with instant replies.", "Natural language understanding for human-like conversations.", "Integrates with CRM and support systems seamlessly.", "Continuously improves through machine learning." ],
    process: [
      { id: 1, title: "Discovery", description: "Understand client needs and chatbot goals." },
      { id: 2, title: "Design", description: "Plan conversational flows and integrations." },
      { id: 3, title: "Development", description: "Build NLP-driven chatbots using AI models." },
      { id: 4, title: "Deployment", description: "Integrate chatbot into platforms and monitor performance." }
    ],
    problem: {
      mockupImage: "/case_study_image/detail_page/10/img-2.png",
      alt: "Chatbot problem mockup",
      challenges: [
        "Customers wait too long for responses.",
        "Support teams are overloaded with repetitive queries.",
        "Businesses struggle to provide 24/7 service."
      ]
    },
    solutions: {
      desc: "We deliver AI-driven chatbots that reduce support burden and enhance customer experience.",
      points: [
        "Automate FAQs and repetitive queries.",
        "Provide instant, 24/7 support across platforms.",
        "Continuously improve with machine learning."
      ],

      mockupImage: "/case_study_image/detail_page/10/img-3.png",
      alt: "Chatbot solution mockup"
    },
    benefits: {
      desc: "Chatbots increase efficiency and customer satisfaction while reducing costs.",
      metrics: [
        { title: "Response Time", value: "80% faster", extra: "vs. traditional support" },
        { title: "Customer Satisfaction", value: "95%", extra: "positive feedback" },
        { title: "Cost Reduction", value: "40%", extra: "savings on support operations" },
        { title: "Availability", value: "24/7", extra: "continuous support" }
      ]
    },
    features: [
      { id: 1, title: "Natural Language Processing", sub_features: ["Understands user intent", "Context-aware conversations"] },
      { id: 2, title: "Omnichannel Support", sub_features: ["Integrates with web, mobile, and social platforms"] }
    ],
    technologies: [
      { icon: "🤖", name: "Dialogflow" },
      { icon: "🧠", name: "TensorFlow" },
      { icon: "☁️", name: "AWS Lex" },
      { icon: "💬", name: "Microsoft Bot Framework" }
    ],
    testimonials: [
      { id: 1, author: "Alonso Coronado", title: "Costa Rica", quote: "Techvelly built a high-quality website for my business and made every change I requested." }
    ],
    faq: [
      { id: 1, question: "Can chatbots integrate with CRM?", answer: "Yes, we integrate with major CRM platforms for seamless workflows." },
      { id: 2, question: "Do chatbots support multiple languages?", answer: "Absolutely, we can configure multi-language support." }
    ]
  },
  {
    id: 2,
    title: "AI Fraud Detection",
    description:
      "Implement advanced AI models to detect and prevent fraudulent activities in real-time. Protect your business and customers with proactive security measures.",
    image: "/case_study_image/case_study_menu/ai_fraud_detection.png",
    alt: "AI Fraud Detection",
    details: [ "Real-time monitoring of transactions for suspicious activity.", "Machine learning models that adapt to new fraud patterns.", "Reduces false positives while catching more fraud." ],
    process: [
      { id: 1, title: "Data Collection", description: "Gather historical transaction data." },
      { id: 2, title: "Model Training", description: "Train AI to recognize fraud patterns." },
      { id: 3, title: "Integration", description: "Deploy fraud detection system into live environment." },
      { id: 4, title: "Monitoring", description: "Continuously update models with new threats." }
    ],
    problem: {
      mockupImage: "/case_study_image/detail_page/8/img-1.png",
      alt: "Fraud problem mockup",
      challenges: [
        "High volume of fraudulent transactions.",
        "Slow manual detection processes.",
        "Financial losses and reputational damage."
      ]
    },
    solutions: {
      desc: "Our AI system stops fraud before it impacts your business.",
      points: [
        "Real-time fraud detection.",
        "Pattern recognition across multiple data points.",
        "Automated alerts and transaction blocking."
      ],
      mockupImage: "/case_study_image/detail_page/8/img-4.png",
      alt: "Fraud solution mockup"
    },
    benefits: {
      desc: "Fraud detection saves money and builds customer trust.",
      metrics: [
        { title: "Fraud Reduction", value: "90%", extra: "decrease in fraud cases" },
        { title: "Detection Speed", value: "Real-time", extra: "instant alerts" }
      ]
    },
    features: [
      { id: 1, title: "Machine Learning Models", sub_features: ["Anomaly detection", "Behavioral analysis"] },
      { id: 2, title: "Custom Alerts", sub_features: ["Instant notification systems", "Automatic blocking"] }
    ],
    technologies: [
      { icon: "🔒", name: "Python ML" },
      { icon: "⚡", name: "Apache Kafka" },
      { icon: "📊", name: "Scikit-learn" }
    ],
    testimonials: [
      { id: 1, author: "Leo Valmont", title: "Portugal", quote: "Working with Techvelly was an amazing experience. They went above and beyond, stayed in touch through every step, and handled all my requests with real care and professionalism." }
    ],
    faq: [
      { id: 1, question: "Does it work with credit cards?", answer: "Yes, it supports various financial transaction systems." },
      { id: 2, question: "How accurate is detection?", answer: "Our models are trained on millions of data points for high accuracy." }
    ]
  },
  {
    id: 3,
    title: "AI Medical Diagnosis",
    description:
      "Leverage AI for accurate and early medical diagnosis. Assist healthcare professionals in identifying diseases and recommending personalized treatments.",
    image: "/case_study_image/case_study_menu/ai_medical_diagnosis.png",
    alt: "AI Medical Diagnosis",
    details: [ "AI analyzes medical images and patient data for early disease detection.", "Supports doctors with evidence-based recommendations.", "Improves diagnostic accuracy and patient outcomes." ],
    process: [
      { id: 1, title: "Data Collection", description: "Gather patient health records and imaging data." },
      { id: 2, title: "Model Training", description: "Train models to detect patterns in medical data." },
      { id: 3, title: "Validation", description: "Ensure accuracy through testing with medical experts." },
      { id: 4, title: "Deployment", description: "Provide AI diagnostic tools to healthcare providers." }
    ],
    problem: {
      mockupImage: "/case_study_image/detail_page/11/img-1.png",
      alt: "Medical problem mockup",
      challenges: [
        "Delayed or inaccurate diagnoses.",
        "High workload for healthcare professionals.",
        "Limited access to specialized expertise."
      ]
    },
    solutions: {
      desc: "AI supports doctors by analyzing data faster and more accurately.",
      points: [
        "Early disease detection through image and data analysis.",
        "Decision support with evidence-based recommendations.",
        "Scalable solutions for hospitals and clinics."
      ],
      mockupImage: "/case_study_image/detail_page/11/img-4.png",
      alt: "Medical solution mockup"

    },
    benefits: {
      desc: "Improved accuracy and faster diagnosis save lives.",
      metrics: [
        { title: "Diagnosis Accuracy", value: "95%", extra: "validated with clinical data" },
        { title: "Time Savings", value: "70%", extra: "reduction in diagnostic time" }
      ]
    },
    features: [
      { id: 1, title: "Medical Imaging Analysis", sub_features: ["X-ray interpretation", "MRI/CT scan analysis"] },
      { id: 2, title: "Predictive Analytics", sub_features: ["Risk scoring", "Preventive care alerts"] }
    ],
    technologies: [
      { icon: "🧬", name: "PyTorch" },
      { icon: "🩺", name: "Keras" },
      { icon: "☁️", name: "Google Cloud AI" }
    ],
    testimonials: [
      { id: 1, author: "Grace", title: "Canada", quote: "I hired Techvelly after seeing their reviews, and they exceeded my expectations. Fast, professional, and great communication throughout — a pleasure to work with!" }
    ],
    faq: [
      { id: 1, question: "Is AI diagnosis FDA-approved?", answer: "We work with solutions that comply with medical regulations and certifications." },
      { id: 2, question: "Can AI replace doctors?", answer: "No, AI supports healthcare professionals but does not replace their expertise." }
    ]
  },
  {
    id: 4,
    title: "AI Recommendation Engine",
    description:
      "Build personalized recommendation systems to boost sales and user satisfaction. Analyze user behavior to suggest relevant products, content, or services.",
    image: "/case_study_image/case_study_menu/ai_recommendation_engine.png",
    alt: "AI Recommendation Engine",
    details: [ "Personalized recommendations based on user behavior and preferences.", "Increases engagement and conversion rates.", "Scalable solutions for e-commerce, media, and more." ],
    process: [
      { id: 1, title: "Data Collection", description: "Collect user activity and transaction data." },
      { id: 2, title: "Model Training", description: "Build collaborative and content-based filtering models." },
      { id: 3, title: "Integration", description: "Deploy recommendation systems across digital channels." },
      { id: 4, title: "Optimization", description: "Continuously refine recommendations with feedback." }
    ],
    problem: {

      mockupImage: "/case_study_image/detail_page/5/img-4.png",
      alt: "Recommendation problem mockup",
      challenges: [
        "Users struggle to find relevant products.",
        "Low engagement on digital platforms.",
        "High cart abandonment rates."
      ]
    },
    solutions: {
      desc: "We personalize user experiences with AI-driven recommendations.",
      points: [
        "Product recommendations tailored to preferences.",
        "Dynamic content suggestions for higher engagement.",
        "Cross-selling and upselling opportunities."
      ],
      mockupImage: "/case_study_image/detail_page/5/img-3.png",
      alt: "Recommendation solution mockup"
    },
    benefits: {
      desc: "Boost sales and improve user satisfaction with personalization.",
      metrics: [
        { title: "Conversion Rate", value: "30%", extra: "increase" },
        { title: "Engagement", value: "2x", extra: "more time spent on site" }
      ]
    },
    features: [
      { id: 1, title: "Personalized Recommendations", sub_features: ["Products, services, and content suggestions"] },
      { id: 2, title: "Adaptive Learning", sub_features: ["Improves with user feedback and behavior"] }
    ],
    technologies: [
      { icon: "📦", name: "Apache Spark" },
      { icon: "🧠", name: "TensorFlow Recommenders" },
      { icon: "☁️", name: "AWS Personalize" }
    ],
    testimonials: [
      { id: 1, author: "Barbara", title: "Usa", quote: "It was a pleasure working with the Techvelly Team. They delivered exactly what I wanted — a professional design, clear communication, and great attention to detail." }
    ],
    faq: [
      { id: 1, question: "Can recommendations work in real time?", answer: "Yes, our engines provide live updates based on user activity." },
      { id: 2, question: "Do you support cross-platform?", answer: "Yes, recommendations can be integrated on web, mobile, and email." }
    ]
  },
  {
    id: 5,
    title: "CRM Dashboard with AI",
    description:
      "Integrate AI into CRM dashboards for predictive analytics, lead scoring, and customer insights. Optimize sales and marketing strategies with data-driven decisions.",
    image: "/case_study_image/case_study_menu/CRM_dashboard.png",
    alt: "CRM Dashboard with AI",
    details: [ "Predictive lead scoring to prioritize sales efforts.", "Customer insights for targeted marketing campaigns.", "Visual dashboards for actionable business intelligence." ],
    process: [
      { id: 1, title: "Requirement Analysis", description: "Identify KPIs and business goals." },
      { id: 2, title: "Data Integration", description: "Connect CRM with sales and marketing data." },
      { id: 3, title: "AI Model Setup", description: "Implement predictive lead scoring and analytics." },
      { id: 4, title: "Dashboard Deployment", description: "Visualize actionable insights." }
    ],
    problem: {
      mockupImage: "/case_study_image/detail_page/7/img-2.png",
      alt: "CRM problem mockup",
      challenges: [
        "Sales teams lack visibility into customer behavior.",
        "Hard to prioritize leads without scoring.",
        "Marketing campaigns lack personalization."
      ]
    },
    solutions: {
      desc: "Our AI-driven CRM dashboard provides deep insights and predictive analytics.",
      points: [
        "Lead scoring and prioritization.",
        "Sales forecasting with predictive analytics.",
        "Customer segmentation and personalization."
      ],
      mockupImage: "/case_study_image/detail_page/7/img-4.png",
      alt: "CRM solution mockup"
    },
    benefits: {
      desc: "Better decisions and increased sales efficiency with AI insights.",
      metrics: [
        { title: "Lead Conversion", value: "45%", extra: "higher success rates" },
        { title: "Forecast Accuracy", value: "80%", extra: "improvement in planning" }
      ]
    },
    features: [
      { id: 1, title: "Predictive Analytics", sub_features: ["Sales forecasting", "Customer churn prediction"] },
      { id: 2, title: "Custom Dashboards", sub_features: ["Real-time KPIs", "Interactive visualizations"] }
    ],
    technologies: [
      { icon: "📊", name: "Tableau + AI" },
      { icon: "🧠", name: "Microsoft Azure AI" },
      { icon: "☁️", name: "Salesforce Einstein" }
    ],
    testimonials: [
      { id: 1, author: "Pedro Benton", title: "Canada", quote: "Great experience! Communication was smooth, and the website turned out even better than we hoped. Excellent work by the Techvelly team!" }
    ],
    faq: [
      { id: 1, question: "Can this work with Salesforce?", answer: "Yes, we integrate with Salesforce, HubSpot, Zoho, and more." },
      { id: 2, question: "Is it customizable?", answer: "Absolutely, dashboards are fully customizable." }
    ]
  },
  {
    id: 6,
    title: "E-commerce AI Solutions",
    description:
      "Enhance e-commerce platforms with AI for dynamic pricing, inventory management, and personalized shopping experiences. Drive conversions and customer loyalty.",
    image: "/case_study_image/case_study_menu/e_commerce.png",
    alt: "E-commerce AI Solutions",
    details: [ "Dynamic pricing strategies for maximum profit.", "AI-driven inventory demand forecasting.", "Personalized shopping and product recommendations." ],
    process: [
      { id: 1, title: "Data Analysis", description: "Review customer behavior and sales history." },
      { id: 2, title: "AI Setup", description: "Build pricing, inventory, and recommendation models." },
      { id: 3, title: "Integration", description: "Deploy AI into e-commerce systems." },
      { id: 4, title: "Optimization", description: "Refine strategies based on results." }
    ],
    problem: {
      mockupImage: "/case_study_image/detail_page/4/img-2.png",
      alt: "E-commerce problem mockup",
      challenges: [
        "Static pricing leads to missed revenue opportunities.",
        "Inventory shortages and overstocking.",
        "Lack of personalized shopping experience."
      ]
    },
    solutions: {
      desc: "AI transforms e-commerce by optimizing pricing, inventory, and personalization.",
      points: [
        "Dynamic pricing strategies for maximum profit.",
        "AI-driven inventory demand forecasting.",
        "Personalized shopping and product recommendations."
      ],
      mockupImage: "/case_study_image/detail_page/4/img-4.png",
      alt: "E-commerce solution mockup"
    },
    benefits: {
      desc: "AI-powered e-commerce boosts revenue and customer loyalty.",
      metrics: [
        { title: "Revenue Growth", value: "25%", extra: "average increase" },
        { title: "Stock Accuracy", value: "90%", extra: "inventory optimization" }
      ]
    },
    features: [
      { id: 1, title: "Dynamic Pricing", sub_features: ["Real-time price adjustments", "Competitor analysis"] },
      { id: 2, title: "Smart Inventory", sub_features: ["Demand forecasting", "Supply chain optimization"] }
    ],
    technologies: [
      { icon: "🛒", name: "Shopify AI" },
      { icon: "🧠", name: "Python ML" },
      { icon: "☁️", name: "Google Cloud Retail AI" }
    ],
    testimonials: [
      { id: 1, author: "Ashley", title: "UK", quote: "Huge thanks to the Techvelly team for the excellent work! Great quality, fast delivery, and clear communication throughout." }
    ],
    faq: [
      { id: 1, question: "Does this integrate with Shopify?", answer: "Yes, we support Shopify, WooCommerce, Magento, and custom platforms." },
      { id: 2, question: "Is AI pricing fair?", answer: "Yes, we ensure AI pricing strategies follow ethical guidelines." }
    ]
  }
];